/* ==========================================================
   Apple Scroll Animation — Scroll-driven canvas frame sequence
   Requires GSAP + ScrollTrigger (loaded before this file).
   One instance is created per section on the page, and torn
   down cleanly on Theme Editor section unload/reload.
   ========================================================== */

(function () {
  'use strict';

  // Registry of live instances keyed by section id, so the
  // Theme Editor lifecycle hooks can find and destroy them.
  const instances = new Map();

  /**
   * AppleScrollAnimation
   * Encapsulates one section: frame preloading, canvas drawing,
   * scroll-driven playback and cleanup.
   */
  class AppleScrollAnimation {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.sectionId = sectionEl.dataset.sectionId;

      // DOM references
      this.stickyWrap = sectionEl.querySelector('[data-apple-scroll-sticky]');
      this.canvas = sectionEl.querySelector('[data-apple-scroll-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;
      this.loaderEl = sectionEl.querySelector('[data-apple-scroll-loader]');
      this.loaderFillEl = sectionEl.querySelector('[data-apple-scroll-loader-fill]');
      this.loaderTextEl = sectionEl.querySelector('[data-apple-scroll-loader-text]');
      this.progressBarEl = sectionEl.querySelector('[data-apple-scroll-progress-bar]');
      this.fpsEl = sectionEl.querySelector('[data-apple-scroll-fps]');
      this.frameNumberEl = sectionEl.querySelector('[data-apple-scroll-frame-number]');
      this.fadeEls = sectionEl.querySelectorAll('[data-apple-scroll-fade]');

      // Config pulled from data attributes (set in Liquid from schema settings)
      this.framesBaseUrl = sectionEl.dataset.framesBaseUrl || '';
      this.frameCount = parseInt(sectionEl.dataset.frameCount, 10) || 0;
      this.frameExtension = (sectionEl.dataset.frameExtension || 'webp').replace(/^\./, '');
      this.startFrame = parseInt(sectionEl.dataset.startFrame, 10) || 1;
      this.endFrame = parseInt(sectionEl.dataset.endFrame, 10) || this.frameCount;
      this.scrollDistance = parseInt(sectionEl.dataset.scrollDistance, 10) || 300;
      this.smoothing = Math.max(0, Math.min(1, (parseInt(sectionEl.dataset.animationSpeed, 10) || 0) / 100));
      this.enableSticky = sectionEl.dataset.enableSticky === 'true';
      this.playMode = sectionEl.dataset.playMode || 'scrub';
      this.debugMode = sectionEl.dataset.debugMode === 'true';
      this.showProgress = sectionEl.dataset.showProgress === 'true';
      this.showFps = sectionEl.dataset.showFps === 'true';
      this.showFrameNumber = sectionEl.dataset.showFrameNumber === 'true';

      // Internal state
      this.images = [];
      this.loadedCount = 0;
      this.currentFrame = 0;
      this.renderFrameIndex = -1; // last frame actually painted, avoids redundant draws
      this.displayFrame = 0; // eased frame used when smoothing > 0
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.scrollTriggerInstance = null;
      this.rafId = null;
      this.resizeObserver = null;
      this.isDestroyed = false;

      // FPS tracking
      this._fpsFrames = 0;
      this._fpsLastTime = performance.now();

      // Bound handlers (so we can remove listeners on destroy)
      this._onResize = this._debounce(() => {
        this.resizeCanvas();
        // The pin's "end" is computed from window.innerHeight, so a
        // viewport resize (or mobile browser chrome show/hide) needs
        // a refresh to keep the scroll track length accurate.
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
      }, 150);

      this._log('Initializing', {
        framesBaseUrl: this.framesBaseUrl,
        frameCount: this.frameCount,
        extension: this.frameExtension,
      });

      this.init();
    }

    /* -------------------------------------------------------
       init
       Entry point: size the canvas, preload frames, then wire
       up scroll-driven playback once assets are ready.
       ------------------------------------------------------- */
    init() {
      if (!this.canvas || !this.framesBaseUrl || this.frameCount < 1) {
        this._log('Missing canvas, base URL, or frame count — aborting init.');
        return;
      }

      this.resizeCanvas();
      window.addEventListener('resize', this._onResize, { passive: true });

      // Keep canvas crisp if the container is resized by theme editor drags
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this._onResize);
        this.resizeObserver.observe(this.canvas.parentElement);
      }

      this.preloadFrames()
        .then(() => {
          if (this.isDestroyed) return;
          this._hideLoader();
          this.renderFrame(0);
          this.initializeScroll();
        })
        .catch((err) => {
          console.error('[AppleScrollAnimation] Failed to preload frames:', err);
          this._hideLoader();
        });
    }

    /* -------------------------------------------------------
       buildFrameUrl(index)
       Builds a candidate URL for a given frame index using a
       specific zero-padding width (4, 3, 2, or no padding).
       ------------------------------------------------------- */
    buildFrameUrl(index, padWidth) {
      const num = padWidth > 0 ? String(index).padStart(padWidth, '0') : String(index);
      return `${this.framesBaseUrl}${num}.${this.frameExtension}`;
    }

    /* -------------------------------------------------------
       detectPadding()
       Auto-detects whether frames are named with 4-digit
       (0001), 3-digit (001), 2-digit (01), or unpadded (1)
       numbering by probing the first frame with each pattern.
       Resolves to the padding width that loads successfully.
       ------------------------------------------------------- */
    detectPadding() {
      const candidates = [4, 3, 2, 0];

      const tryLoad = (url) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => reject(false);
          img.src = url;
        });

      const attempt = (i) => {
        if (i >= candidates.length) {
          // Fall back to 4-digit padding if nothing resolved cleanly;
          // preloadFrames() will still surface per-image load errors.
          return Promise.resolve(4);
        }
        const padWidth = candidates[i];
        const url = this.buildFrameUrl(this.startFrame, padWidth);
        return tryLoad(url)
          .then(() => padWidth)
          .catch(() => attempt(i + 1));
      };

      return attempt(0);
    }

    /* -------------------------------------------------------
       preloadFrames()
       Detects numbering scheme, then loads every frame in the
       configured range into an in-memory Image array. Updates
       the loader UI with progress as each image resolves.
       Returns a promise that resolves once loading completes.
       ------------------------------------------------------- */
    preloadFrames() {
      return this.detectPadding().then((padWidth) => {
        this.padWidth = padWidth;
        const total = this.endFrame - this.startFrame + 1;
        this.images = new Array(total);

        const loadOne = (i) => {
          return new Promise((resolve) => {
            const frameIndex = this.startFrame + i;
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => {
              this.images[i] = img;
              this.loadedCount++;
              this._updateLoaderProgress(this.loadedCount, total);
              resolve();
            };
            img.onerror = () => {
              // Keep going even if a single frame 404s — do not
              // block the whole sequence over one missing file.
              this.loadedCount++;
              this._updateLoaderProgress(this.loadedCount, total);
              this._log('Frame failed to load:', frameIndex);
              resolve();
            };
            img.src = this.buildFrameUrl(frameIndex, padWidth);
          });
        };

        // Load the first frame eagerly and with priority, then the
        // rest concurrently in small batches to avoid saturating
        // the connection on slower mobile networks.
        const batchSize = 12;
        const indices = Array.from({ length: total }, (_, i) => i);

        const runBatches = async () => {
          await loadOne(0);
          for (let start = 1; start < indices.length; start += batchSize) {
            const batch = indices.slice(start, start + batchSize);
            await Promise.all(batch.map(loadOne));
          }
        };

        return runBatches();
      });
    }

    /* -------------------------------------------------------
       resizeCanvas()
       Resizes the canvas backing store for the current
       devicePixelRatio while keeping CSS dimensions controlled
       by the stylesheet, so drawing stays crisp on retina
       screens without layout thrashing.
       ------------------------------------------------------- */
    resizeCanvas() {
      if (!this.canvas) return;
      this.dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.canvas.getBoundingClientRect();
      const width = Math.max(rect.width, 1);
      const height = Math.max(rect.height, 1);

      this.canvas.width = Math.round(width * this.dpr);
      this.canvas.height = Math.round(height * this.dpr);

      if (this.ctx) {
        this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      }

      // Force a repaint of the current frame at the new resolution.
      this.renderFrameIndex = -1;
      this.renderFrame(this.currentFrame);
    }

    /* -------------------------------------------------------
       renderFrame(index)
       Draws the given frame index onto the canvas, centred and
       scaled to fill the canvas box while preserving aspect
       ratio. Skips redundant draws of the same frame.
       ------------------------------------------------------- */
    renderFrame(index) {
      if (!this.ctx || !this.images.length) return;

      const clamped = Math.max(0, Math.min(this.images.length - 1, Math.round(index)));
      if (clamped === this.renderFrameIndex) return;

      const img = this.images[clamped];
      if (!img || !img.complete || img.naturalWidth === 0) return;

      const cssWidth = this.canvas.width / this.dpr;
      const cssHeight = this.canvas.height / this.dpr;

      this.ctx.clearRect(0, 0, cssWidth, cssHeight);

      // "Contain" fit so wave rings stay centred and undistorted.
      const scale = Math.min(cssWidth / img.naturalWidth, cssHeight / img.naturalHeight);
      const drawWidth = img.naturalWidth * scale;
      const drawHeight = img.naturalHeight * scale;
      const offsetX = (cssWidth - drawWidth) / 2;
      const offsetY = (cssHeight - drawHeight) / 2;

      this.ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      this.renderFrameIndex = clamped;
      this.currentFrame = clamped;

      if (this.showFrameNumber && this.frameNumberEl) {
        this.frameNumberEl.textContent = `Frame: ${clamped + 1} / ${this.images.length}`;
      }

      this._trackFps();
    }

    /* -------------------------------------------------------
       updateFrame(progress)
       Converts a 0–1 scroll progress value into a target frame
       index, applies optional smoothing/easing, and schedules
       a render via requestAnimationFrame.
       ------------------------------------------------------- */
    updateFrame(progress) {
      const clampedProgress = Math.max(0, Math.min(1, progress));
      const targetFrame = clampedProgress * (this.images.length - 1);

      if (this.showProgress && this.progressBarEl) {
        this.progressBarEl.style.width = `${clampedProgress * 100}%`;
      }

      this._updateTextFade(clampedProgress);

      if (this.smoothing <= 0) {
        this._scheduleRender(targetFrame);
        return;
      }

      // Ease the displayed frame toward the scroll-driven target
      // for a smoother, less mechanical feel (higher = smoother).
      const easeFactor = 1 - this.smoothing * 0.9;
      this.displayFrame += (targetFrame - this.displayFrame) * Math.max(0.05, easeFactor);
      this._scheduleRender(this.displayFrame);
    }

    /* -------------------------------------------------------
       _scheduleRender(index)
       Batches render calls into requestAnimationFrame to avoid
       layout thrashing from rapid scroll events.
       ------------------------------------------------------- */
    _scheduleRender(index) {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.renderFrame(index));
    }

    /* -------------------------------------------------------
       _updateTextFade(progress)
       Fades and lifts the text column out as the animation
       nears completion, then back in near the very start, so
       copy does not visually collide with the pinned visual.
       ------------------------------------------------------- */
    _updateTextFade(progress) {
      if (!this.fadeEls || !this.fadeEls.length) return;
      const shouldHide = progress > 0.12 && progress < 0.92;
      this.fadeEls.forEach((el) => {
        el.classList.toggle('is-hidden', shouldHide);
      });
    }

    /* -------------------------------------------------------
       initializeScroll()
       Wires GSAP ScrollTrigger to pin the section and scrub
       playback in lockstep with scroll position, forward on
       scroll down and reversed on scroll up.
       ------------------------------------------------------- */
    initializeScroll() {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        console.error('[AppleScrollAnimation] GSAP/ScrollTrigger not found.');
        return;
      }

      gsap.registerPlugin(ScrollTrigger);

      const state = { progress: 0 };

      this.scrollTriggerInstance = ScrollTrigger.create({
        trigger: this.section,
        start: 'top top',
        // `end` is computed in pixels from the current viewport
        // height rather than "+=N%" (which would be a percentage
        // of the trigger's own box — and this section intentionally
        // has no fixed height, since GSAP reserves the scroll track
        // itself via pinSpacing). A function form re-evaluates on
        // ScrollTrigger.refresh(), so it stays correct on resize.
        end: () => `+=${window.innerHeight * (this.scrollDistance / 100)}`,
        pin: this.enableSticky ? this.stickyWrap : false,
        // Default (true): GSAP inserts a spacer sized to the pin
        // duration, so content after this section — including the
        // footer — is pushed down by exactly that much instead of
        // being covered while the visual sits pinned.
        pinSpacing: true,
        scrub: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          state.progress = self.progress;
          this.updateFrame(self.progress);

          if (this.playMode === 'once' && self.progress >= 1) {
            this.scrollTriggerInstance.disable(false, false);
          }
        },
      });

      this._log('ScrollTrigger initialized', this.scrollTriggerInstance);
    }

    /* -------------------------------------------------------
       _trackFps()
       Lightweight FPS counter, updated once per second, shown
       only when debug/showFps is enabled via section settings.
       ------------------------------------------------------- */
    _trackFps() {
      if (!this.showFps || !this.fpsEl) return;
      this._fpsFrames++;
      const now = performance.now();
      const elapsed = now - this._fpsLastTime;
      if (elapsed >= 1000) {
        const fps = Math.round((this._fpsFrames * 1000) / elapsed);
        this.fpsEl.textContent = `FPS: ${fps}`;
        this._fpsFrames = 0;
        this._fpsLastTime = now;
      }
    }

    /* -------------------------------------------------------
       _updateLoaderProgress(loaded, total)
       Updates the loading bar/label and reveals the section
       once every frame has attempted to load.
       ------------------------------------------------------- */
    _updateLoaderProgress(loaded, total) {
      const pct = Math.round((loaded / total) * 100);
      if (this.loaderFillEl) this.loaderFillEl.style.width = `${pct}%`;
      if (this.loaderTextEl) this.loaderTextEl.textContent = `Loading ${pct}%`;
    }

    /* -------------------------------------------------------
       _hideLoader()
       Fades out the loading overlay once frames are ready.
       ------------------------------------------------------- */
    _hideLoader() {
      if (this.loaderEl) this.loaderEl.classList.add('is-loaded');
    }

    /* -------------------------------------------------------
       _debounce(fn, wait)
       Simple debounce helper used for resize handling.
       ------------------------------------------------------- */
    _debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }

    /* -------------------------------------------------------
       _log(...args)
       Console logging gated behind the debug_mode setting so
       production stores stay quiet.
       ------------------------------------------------------- */
    _log(...args) {
      if (this.debugMode) {
        console.log('[AppleScrollAnimation]', ...args);
      }
    }

    /* -------------------------------------------------------
       destroy()
       Full teardown: cancels pending frames, removes listeners,
       kills the ScrollTrigger instance, and clears image
       references so memory can be reclaimed. Called on Theme
       Editor section unload/reload and SPA-style navigations.
       ------------------------------------------------------- */
    destroy() {
      this.isDestroyed = true;

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }

      window.removeEventListener('resize', this._onResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
        this.scrollTriggerInstance = null;
      }

      this.images = [];
      this._log('Destroyed');
    }
  }

  /* -------------------------------------------------------
     mountSection(sectionEl)
     Creates (or re-creates) an AppleScrollAnimation instance
     for a given section element and stores it in the registry.
     ------------------------------------------------------- */
  function mountSection(sectionEl) {
    if (!sectionEl) return;
    const id = sectionEl.dataset.sectionId;
    if (instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
    instances.set(id, new AppleScrollAnimation(sectionEl));
  }

  /* -------------------------------------------------------
     mountAll()
     Mounts every instance of the section present on the page.
     ------------------------------------------------------- */
  function mountAll() {
    document.querySelectorAll('[data-apple-scroll-section]').forEach(mountSection);
  }

  /* -------------------------------------------------------
     Bootstrapping
     ------------------------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  /* -------------------------------------------------------
     Shopify Theme Editor lifecycle hooks
     ------------------------------------------------------- */
  document.addEventListener('shopify:section:load', (event) => {
    const sectionEl = event.target.querySelector('[data-apple-scroll-section]');
    if (sectionEl) mountSection(sectionEl);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionEl = event.target.querySelector('[data-apple-scroll-section]');
    if (!sectionEl) return;
    const id = sectionEl.dataset.sectionId;
    if (instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const sectionEl = event.target.closest('[data-apple-scroll-section]');
    if (sectionEl && window.ScrollTrigger) {
      requestAnimationFrame(() => ScrollTrigger.refresh());
    }
  });
})();